import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';

import {
  genericErrorShowToastOptions,
  useGenericErrorMessage,
} from '../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { CellClickedEvent, ColDef, SelectionChangedEvent } from 'ag-grid-community';
import { CaretPosition, Variant } from '../../components/Button/Button.types';
import Table from '../../components/Table/Table';
import {
  useAddQsrDeptMaster, useAddQsrDeptXref, useModifyXrefsDepartment,
  useQsrDeptMaster,
  useQsrDeptXref, useRemoveQsrDeptMaster,
  useRemoveQsrDeptXref, useUpdateQsrDeptMaster,
} from '../../services/qsrDept/qsrDept.service';
import {
  CreateQsrDeptXrefRequest, ModifyXrefsDepartmentRequest,
  QsrDeptMaster, QsrDeptMasterFormErrors,
  QsrDeptXref,
  QsrOptionSelect,
  RemoveQsrDeptXrefRequest,
} from '../../services/qsrDept/qsrDept.types';
import {
  QsrDeptPage,
  DropdownWrapper,
  StyledExtendedHeader,
  StyledLink,
  Title,
  TableWrapper,
} from './QsrDept.styles';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { EditCell } from '../../components/Table/EditCellRenderer/EditCellRenderer.styles';
import { DeleteButton, StyledButtons } from '../RawProdGINs/RawProdGins.style';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import theme from '../../theme';
import Button from '../../components/Button/Button';
import Modal from '../../components/Modal/Modal';
import { showToast } from '../../components/Toast/Toast';
import { ToastTypes } from '../../components/Toast/Toast.types';
import { StyledTextButton, TextButtonsWrapper } from '../MandatoryInventory/MandatoryInventory.styles';
import { useClasses } from '../../services/class/class.service';
import { Class } from '../../services/class/class.types';
import useNavigateWithToast from '../../utils/useNavigateWithToast/useNavigateWithToast';
import { useClassBases } from '../../services/base/base.service';
import { ClassBase } from '../../services/base/base.types';
import { useClassSizes } from '../../services/size/size.service';
import { ClassSize } from '../../services/size/size.types';
import { useProducts } from '../../services/product/product.service';
import { Product } from '../../services/product/product.types';
import Combobox from '../../components/Combobox/Combobox';
import { Buttons } from '../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import RemoveQsrXrefModal from './Modals/RemoveQsrXrefModal';
import AddQsrDeptMasterModal from './Modals/AddQsrDeptMasterModal';
import UpdateQsrDeptMasterModal from './Modals/UpdateQsrDeptMasterModal';
import RemoveQsrDeptMasterModal from './Modals/RemoveQsrDeptMasterModal';
import AddNewQsrXrefModal from './Modals/AddNewQsrXrefModal';

const QsrDept: FC = () => {
  const tableRef = useRef<AgGridReact>(null);
  const navigateWithToast = useNavigateWithToast();

  const { data, isError: isQsrDeptMasterError, error: qsrDeptMasterError, refetch } = useQsrDeptMaster(true);
  const [selectedDeptXref, setSelectedDeptXref] = useState<QsrDeptXref | null>(null);
  const [qsrDeptMasters, setQsrDeptMasters] = useState<QsrDeptMaster[]>([]);
  const [qsrDeptXref, setQsrDeptXref] = useState<QsrDeptXref[]>([]);
  const [selectedQsrDeptMaster, setSelectedQsrDeptMaster] = useState<QsrOptionSelect | null>(null);
  const [removeXrefModal, setRemoveXrefModal] = useState<boolean>(false);
  const [removeMasterModal, setRemoveMasterModal] = useState<boolean>(false);

  const closeRemoveMasterModal = () => {
    setRemoveMasterModal(false);
  };

  /**
   * Update Qsr Dept Master Modal
   */
  const [updateQsrDeptMasterModal, setUpdateQsrDeptMasterModal] = useState<boolean>(false);
  const [updateQsrMasterDescription, setUpdateQsrMasterDescription] = useState('');
  const [updateQsrMasterClassNo, setUpdateQsrMasterClassNo] = useState<number | null>(null);
  const [updateFormErrors, setUpdateFormErrors] = useState<QsrDeptMasterFormErrors>({
    departmentDescription: '',
    classDescription: '',
  });

  const {
    mutate: updateQsrDeptMaster,
    isError: isUpdateMasterError,
    error: updateMasterError,
  } = useUpdateQsrDeptMaster();

  const closeUpdateQsrDeptMasterModal = () => {
    setUpdateQsrDeptMasterModal(false);
    clearUpdateMasterErrors();
  };

  const closeAddQsrDeptXrefModal = () => {
    setAddNewQsrDeptXrefModal(false);
    clearAddXref();
  };

  /**
   * Add New Qsr Dept Master Modal
   */
  const [addNewQsrDeptMasterModal, setAddNewQsrDeptMasterModal] = useState<boolean>(false);
  const [qsrDeptMasterDescription, setQsrDeptMasterDescription] = useState('');
  const [qsrDeptMasterClassNo, setQsrDeptMasterClassNo] = useState<number | null>(null);
  const [formErrors, setFormErrors] = useState<QsrDeptMasterFormErrors>({
    departmentDescription: '',
    classDescription: '',
  });

  const { mutate: addQsrDeptMaster, isError: isAddMasterError, error: addMasterError } = useAddQsrDeptMaster();

  /**
   * Add New Qsr Dept Xref Modal
   */
  const [addNewQsrDeptXrefModal, setAddNewQsrDeptXrefModal] = useState<boolean>(false);
  const [selectedClassToAddQsrXref, setSelectedClassToAddQsrXref] = useState<number | null>(null);

  const { mutate: addQsrDeptXref, isError: isAddXrefError, error: addXrefError } = useAddQsrDeptXref();

  const closeAddQsrDeptMasterModal = () => {
    setAddNewQsrDeptMasterModal(false);
    clearAddMasterErrors();
    setQsrDeptMasterClassNo(null);
  };

  /**
   * Modify Qsr Xref Department
   */
  const [modifyXrefDepartmentModal, setModifyXrefDepartmentModal] = useState<boolean>(false);
  const [selectedXrefs, setSelectedXrefs] = useState<QsrDeptXref[]>([]);
  const [selectedDesiredQsrMaster, setSelectedDesiredQsrMaster] = useState<QsrOptionSelect | null>(null);

  const {
    mutate: modifyXrefsDepartment,
    isError: isModifyXrefsDepartmentError,
    error: modifyXrefsDepartmentError,
  } = useModifyXrefsDepartment();

  const closeModifyDepartmentModal = () => {
    setModifyXrefDepartmentModal(false);
    setSelectedDesiredQsrMaster(null);
  };

  const isDisabled = selectedClassToAddQsrXref === null;

  const { data: qsrDeptXrefData, refetch: refetchXrefs } = useQsrDeptXref(
    {
      deptId: selectedQsrDeptMaster?.id ?? null,
    },
    true,
  ); // Get items based on dept

  const { data: classes } = useClasses(
    true,
    undefined,
  ); // Get items based on dept

  const classOptions =
    classes?.data?.map((item: Class) => {
      return { value: item?.classNo?.toString(), label: `${item?.classNo} ~ ${item?.description}` };
    }) || [];

  const { data: bases, isError: isBasesError, error: basesError } = useClassBases(
    { classNo: selectedClassToAddQsrXref?.toString(), status: undefined },
    selectedClassToAddQsrXref !== null,
  );

  const [selectedBase, setSelectedBase] = useState<number | undefined>(undefined);

  const baseOptions =
    bases?.data?.map((item: ClassBase) => {
      return { value: item?.baseNo?.toString(), label: `${item?.baseNo} ~ ${item?.baseData.description}` };
    }) || [];

  const classSizeCompareFn = (itemA: { value: string, label: string }, itemB: { value: string, label: string }) => {
    if (+itemA.value > +itemB.value) {
      return 1;
    }
    if (+itemA.value < +itemB.value) {
      return -1;
    }

    return 0;
  };

  const { data: sizes, isError: isSizesError, error: sizesError } = useClassSizes(
    { classNo: selectedClassToAddQsrXref?.toString() || '', status: undefined },
    selectedClassToAddQsrXref !== null,
  );
  const [selectedSize, setSelectedSize] = useState<number | undefined>(undefined);
  const sizeOptions =
    sizes?.data?.map((item: ClassSize) => {
      return { value: item?.sizeNo?.toString(), label: `${item?.sizeNo} ~ ${item?.sizeData.description}` };
    }).sort(classSizeCompareFn) || [];

  const { data: products, isError: isProductsError, error: productsError } = useProducts(
    selectedClassToAddQsrXref !== null,
    { classNo: selectedClassToAddQsrXref ?? undefined, statusNo: undefined },
  );
  const [selectedProduct, setSelectedProduct] = useState<number | undefined>(undefined);
  const productOptions =
    products?.data?.map((item: Product) => {
      return { value: item?.productNo?.toString(), label: `${item?.productNo} ~ ${item?.description}` };
    }) || [];

  const clearAddXref = () => {
    setSelectedClassToAddQsrXref(null);
    setSelectedBase(() => undefined);
    setSelectedSize(() => undefined);
    setSelectedProduct(() => undefined);
  };

  const handleClassSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedClassToAddQsrXref(+event.target.value);
    setSelectedBase(() => undefined);
    setSelectedSize(() => undefined);
    setSelectedProduct(() => undefined);
  };

  const handleBaseSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedBase(handleUndefinedEvent(event));
  };

  const handleSizeSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedSize(handleUndefinedEvent(event));
  };

  const handleProductSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedProduct(handleUndefinedEvent(event));
  };

  const handleUndefinedEvent = (e: ChangeEvent<HTMLSelectElement>): number | undefined => {
    return e.target.value.length === 0 ? undefined : +e.target.value;
  };


  const { mutate: removeQsrDeptXref, isError: isRemoveXrefError, error: removeError } = useRemoveQsrDeptXref();
  const {
    mutate: removeQsrDeptMaster,
    isError: isRemoveMasterError,
    error: removeMasterError,
  } = useRemoveQsrDeptMaster();

  const allQsrDeptMaster = data?.data || [];
  const allQsrDeptXref = qsrDeptXrefData?.data || [];

  const onCellClicked = (event: CellClickedEvent) => {
    setSelectedDeptXref(event.data as QsrDeptXref);
    setRemoveXrefModal(true);
  };

  const closeRemoveXrefModal = () => {
    setRemoveXrefModal(false);
  };

  useGenericErrorMessage([
    { isError: isQsrDeptMasterError, error: qsrDeptMasterError },
    { isError: isRemoveXrefError, error: removeError },
    { isError: isAddMasterError, error: addMasterError },
    { isError: isUpdateMasterError, error: updateMasterError },
    { isError: isRemoveMasterError, error: removeMasterError },
    { isError: isBasesError, error: basesError },
    { isError: isSizesError, error: sizesError },
    { isError: isProductsError, error: productsError },
    { isError: isAddXrefError, error: addXrefError },
    { isError: isModifyXrefsDepartmentError, error: modifyXrefsDepartmentError },
  ]);

  const handleQsrDeptMasterDescription = (e: ChangeEvent<HTMLInputElement>) => {
    setQsrDeptMasterDescription(e.target.value);
  };

  const handleQsrDeptMasterClassNo = (e: ChangeEvent<HTMLSelectElement>) => {
    setQsrDeptMasterClassNo(e.target.value.length === 0 ? null : +e.target.value);
  };

  const handleUpdateQsrDeptMasterDescription = (event: ChangeEvent<HTMLInputElement>) => {
    setUpdateQsrMasterDescription(event.target.value);
  };

  const handleUpdateQsrDeptMasterClassNo = (e: ChangeEvent<HTMLSelectElement>) => {
    setUpdateQsrMasterClassNo(e.target.value.length === 0 ? null : +e.target.value);
  };

  const clearAddMasterErrors = () => {
    setFormErrors({
      departmentDescription: '',
      classDescription: '',
    });
  };

  const clearUpdateMasterErrors = () => {
    setUpdateFormErrors({
      departmentDescription: '',
      classDescription: '',
    });
  };

  useEffect(() => {
    setQsrDeptMasters(allQsrDeptMaster);
  }, [data]);

  useEffect(() => {
    setQsrDeptXref(allQsrDeptXref);
  }, [qsrDeptXrefData]);

  const addNewQsrDeptMaster = () => {
    clearAddMasterErrors();

    const isDescriptionEmptyOrOverLimit = qsrDeptMasterDescription.length === 0 || qsrDeptMasterDescription.length > 200;

    const isClassNoInUse = qsrDeptMasters.some(value => {
      return value.defaultClassNo === qsrDeptMasterClassNo;
    }) && qsrDeptMasterClassNo !== null;

    if (isDescriptionEmptyOrOverLimit) {
      setFormErrors((formErrors) => {
        return { ...formErrors, departmentDescription: 'Please enter a valid description' };
      });
    }

    if (isClassNoInUse) {
      setFormErrors((formErrors) => {
        return { ...formErrors, classDescription: 'This Class is already assigned' };
      });
    }

    if (isClassNoInUse || isDescriptionEmptyOrOverLimit) {
      return;
    }

    const addQsrMaster: QsrDeptMaster = {
      deptId: null,
      defaultClassDescription: null,
      description: qsrDeptMasterDescription,
      defaultClassNo: qsrDeptMasterClassNo ?? null,
    };

    addQsrDeptMaster(
      addQsrMaster,
      {
        onSuccess: async ({ data }) => {
          showToast({ label: 'QSR Department Master added.', id: 'addQsrDeptMasterSuccess' });
          setQsrDeptMasters((prevState) => [...prevState, data]);
          setSelectedQsrDeptMaster(_prevState => qsrDeptMasterIntoOptions(data));
          closeAddQsrDeptMasterModal();
        },
        onError: () => {
          showToast({
            ...genericErrorShowToastOptions,
            id: 'AddNewQsrMasterError',
            label: 'Error adding new QSR Department Master.',
          });
        },
      },
    );
  };

  const handleUpdateQsrDeptMaster = () => {
    clearUpdateMasterErrors();

    const isDescriptionEmptyOrOverLimit = updateQsrMasterDescription.length === 0 || updateQsrMasterDescription.length > 200;

    const isClassNoInUse = qsrDeptMasters.some(value => {
      return value.defaultClassNo === updateQsrMasterClassNo && value.deptId !== selectedQsrDeptMaster?.id;
    }) && updateQsrMasterClassNo !== null;

    if (isDescriptionEmptyOrOverLimit) {
      setUpdateFormErrors((formErrors) => {
        return { ...formErrors, departmentDescription: 'Please enter a valid description' };
      });
    }

    if (isClassNoInUse) {
      setUpdateFormErrors((formErrors) => {
        return { ...formErrors, classDescription: 'This Class is already assigned' };
      });
    }

    if (isClassNoInUse || isDescriptionEmptyOrOverLimit) {
      return;
    }

    const updateQsrMaster: QsrDeptMaster = {
      deptId: selectedQsrDeptMaster?.id ?? null,
      defaultClassDescription: null,
      description: updateQsrMasterDescription,
      defaultClassNo: updateQsrMasterClassNo,
    };

    updateQsrDeptMaster(
      updateQsrMaster,
      {
        onSuccess: async ({ data }) => {
          await refetch();
          await refetchXrefs();
          showToast({ label: 'QSR Department Master updated.', id: 'updateQsrDeptMasterSuccess' });
          setSelectedQsrDeptMaster(_prevState => qsrDeptMasterIntoOptions(data));
          closeUpdateQsrDeptMasterModal();
        },
        onError: () => {
          showToast({
            ...genericErrorShowToastOptions,
            id: 'UpdateNewQsrMasterError',
            label: 'Error updating QSR Department Master.',
          });
        },
      },
    );
  };

  const handleRemoveQsrMaster = () => {
    removeQsrDeptMaster(selectedQsrDeptMaster?.id ?? 0, {
      onSuccess: async () => {
        navigateWithToast(`/tables/qsrdept`, {
          label: 'QSR Department Master has been removed successfully',
          id: 'qsrMasterRemovalSuccess',
        });
        setSelectedQsrDeptMaster(null);
        await refetch();
        await refetchXrefs();
        setRemoveMasterModal(false);
      },
      onError: () => {
        showToast({
          ...genericErrorShowToastOptions,
          id: 'qsrMasterRemovalError',
          label: 'Error removing QSR Department Master.',
        });
      },
    });
  };

  const onRemoveRow = () => {
    if (selectedDeptXref !== null) {
      const xrefToRemove: RemoveQsrDeptXrefRequest = {
        deptId: selectedDeptXref.departmentId,
        mmsClassNo: selectedDeptXref.classNo,
        mmsBaseNo: selectedDeptXref.baseNo,
        mmsSizeNo: selectedDeptXref.sizeNo,
        mmsProdNo: selectedDeptXref.prodNo,
      };

      removeQsrDeptXref(xrefToRemove, {
        onSuccess: () => {
          showToast({
            label: 'QSR Department Xref Deleted',
            id: 'removeQsrXrefSuccess',
          });
          closeRemoveXrefModal();
          refetch();
          refetchXrefs();
          setSelectedDeptXref(null);
        },
      });
    } else {
      showToast({
        label: 'No Qsr Xref was selected',
        id: 'noQsrXrefSelectedError',
        toastType: ToastTypes.Error,
      });
    }
  };

  const handleAddQsrDeptXref = () => {
    // @ts-ignore: Objects are already validated at this point
    const addQsrXref: CreateQsrDeptXrefRequest = { deptId: selectedQsrDeptMaster?.id ?? null, mmsClassNo: +selectedClassToAddQsrXref, mmsBaseNo: +selectedBase, mmsSizeNo: +selectedSize, mmsProdNo: +selectedProduct, };

    addQsrDeptXref(
      addQsrXref,
      {
        onSuccess: async () => {
          await refetch();
          await refetchXrefs();
          showToast({ label: 'QSR Department Xref added.', id: 'addQsrDeptXrefSuccess' });
          closeAddQsrDeptXrefModal();
        },
        onError: () => {
          showToast({
            ...genericErrorShowToastOptions,
            id: 'AddNewQsrXrefError',
            label: 'Error adding QSR Department Xref.',
          });
        },
      },
    );
  };

  const handleModifyXrefsDepartments = () => {
    const modifyXrefsDepartmentRequest: ModifyXrefsDepartmentRequest = {
      desiredDeptId: selectedDesiredQsrMaster?.id,
      xrefsToUpdate: selectedXrefs.map((xref) => {
        return {
          deptId: xref.departmentId,
          mmsClassNo: xref.classNo,
          mmsBaseNo: xref.baseNo,
          mmsSizeNo: xref.sizeNo,
          mmsProdNo: xref.prodNo,
        };
      }),
    };

    modifyXrefsDepartment(
      modifyXrefsDepartmentRequest,
      {
        onSuccess: () => {
          closeModifyDepartmentModal();
          refetch();
          refetchXrefs();
          showToast({ label: 'Departments updated.', id: 'updateDepartmentSuccess' });
          setSelectedQsrDeptMaster(_prevState => selectedDesiredQsrMaster);
        },
        onError: () => {
          showToast({
            ...genericErrorShowToastOptions,
            id: 'ModifyDepartmentError',
            label: 'Error modifying Department for Xrefs',
          });
        },
      },
    );
  };

  const RemoveRowData = () => {
    return (
      <EditCell>
        <DeleteButton>
          <DeleteOutlinedIcon
            fontSize="inherit"
            htmlColor={theme.colors.red}
            style={{ cursor: 'pointer', height: '100%' }}
          />
        </DeleteButton>
      </EditCell>
    );
  };

  const qsrDeptXrefColumnDefs: ColDef[] = [
    {
      minWidth: 70,
      maxWidth: 70,
      flex: 1,
      headerName: 'Select',
      checkboxSelection: true,
      floatingFilter: false,
      sortable: false,
    },
    {
      headerName: 'Department',
      field: 'department',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      flex: 1,
      minWidth: 180,
    },

    {
      headerName: 'Class',
      field: 'classDescription',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      flex: 1,
      minWidth: 180,
    },

    {
      headerName: 'Base',
      field: 'baseDescription',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      flex: 1,
      minWidth: 180,
    },
    {
      headerName: 'Size',
      field: 'sizeDescription',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      flex: 1,
      minWidth: 180,
    },
    {
      headerName: 'Prod',
      field: 'prodDescription',
      flex: 1,
      minWidth: 180,
      resizable: false,
    },
    {
      headerName: 'Delete',
      field: 'remove',
      flex: 0.30,
      floatingFilter: false,
      minWidth: 45,
      resizable: false,
      cellRenderer: RemoveRowData,
      onCellClicked: onCellClicked,
      tooltipValueGetter: _params => 'Remove Xref'
    },
  ];

  const buildQsrMasterLabel = (qsrDeptMaster: QsrDeptMaster) => {
    return qsrDeptMaster.defaultClassNo === null ? `${qsrDeptMaster.deptId} ~ ${qsrDeptMaster.description?.trim()}` : `${qsrDeptMaster.deptId} ~ ${qsrDeptMaster.description?.trim()} — ${qsrDeptMaster.defaultClassNo} ${qsrDeptMaster.defaultClassDescription}`;
  };

  const qsrDeptMasterIntoOptions = (qsrDeptMaster: QsrDeptMaster) => {
    return {
      id: qsrDeptMaster.deptId,
      label: buildQsrMasterLabel(qsrDeptMaster),
      description: qsrDeptMaster.description,
      isNotAssigned: qsrDeptMaster.defaultClassNo === null,
      classNo: qsrDeptMaster.defaultClassNo,
    };
  };

  const handleXrefSelection = (event: SelectionChangedEvent) => {
    setSelectedXrefs(event.api.getSelectedRows());
  };

  const validateCbspToAddQsrXref = () => {
    return !selectedClassToAddQsrXref
      || selectedBase === undefined
      || selectedSize === undefined
      || selectedProduct === undefined;
  };

  return (
    <>
      <StyledExtendedHeader>
        <StyledLink to="/tables" caret={CaretPosition.Left} label="All Tables" id="skipNavTarget" />
        <Title data-testid={'QsrDepartmentPageTitle'}>QSR Department</Title>
        <DropdownWrapper>
          <Combobox label={'QSR Department Master'}
                    options={qsrDeptMasters.map(qsrDeptMasterIntoOptions)}
                    value={selectedQsrDeptMaster || null}
                    setSelectedOption={(_event: any, newValue: QsrOptionSelect | null) => {
                      setSelectedXrefs([]);
                      setSelectedQsrDeptMaster(newValue);
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return option.id === value?.id;
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        {option.label}
                        {option.isNotAssigned ? <CloseIcon color="error" sx={{ fontSize: 15, paddingLeft: '5px' }} /> :
                          <CheckIcon color="success" sx={{ paddingLeft: '5px' }} />}
                      </li>
                    )}
          />
        </DropdownWrapper>
        <TextButtonsWrapper>
          <StyledTextButton label="Add New QSR Dept. Master" variant={Variant.Text}
                            onClick={() => setAddNewQsrDeptMasterModal(true)} testId={'AddNewQsrMasterLink'} />
          {selectedQsrDeptMaster?.id ? (
            <StyledTextButton label="Update" testId={'UpdateQsrMasterLink'} variant={Variant.Text} onClick={() => {
              setUpdateQsrMasterDescription(selectedQsrDeptMaster?.description?.trim() ?? '');
              setUpdateQsrMasterClassNo(selectedQsrDeptMaster?.classNo);
              setUpdateQsrDeptMasterModal(true);
            }} />
          ) : (
            <></>
          )}
          {selectedQsrDeptMaster ? (
            <StyledTextButton label="Delete" testId={'RemoveQsrMasterLink'} variant={Variant.Text}
                              onClick={() => setRemoveMasterModal(true)} />
          ) : (
            <></>
          )}
        </TextButtonsWrapper>
      </StyledExtendedHeader>
      <QsrDeptPage>
        <TableWrapper>
          <Table
            ref={tableRef}
            rowData={qsrDeptXref}
            columnDefs={qsrDeptXrefColumnDefs}
            suppressRowClickSelection={true}
            showHeader={true}
            headerTitle="QSR Department Xref"
            returnedRecords={qsrDeptXref.length ?? 0}
            height={'100%'}
            width={'100%'}
            rowSelection="multiple"
            onSelectionChanged={handleXrefSelection}
            headerButtons={[{
              label: 'Add New',
              isSmall: true,
              variant: Variant.Secondary,
              onClick: () => setAddNewQsrDeptXrefModal(true),
              disabled: selectedQsrDeptMaster === null,
              testId: 'AddNewXrefButton',
            }]}
          />
        </TableWrapper>
        <Buttons>
          <Button
            label={
              'Modify' +
              '\nDepartment'
            }
            variant={Variant.PrimaryWordWrap}
            testId="ModifyDepartmentButton"
            disabled={selectedXrefs.length === 0 || selectedQsrDeptMaster === null}
            onClick={() => {
              setModifyXrefDepartmentModal(true);
            }}
          />
        </Buttons>
      </QsrDeptPage>
      <RemoveQsrXrefModal
        open={removeXrefModal}
        onClose={() => {setRemoveXrefModal(false);}}
        onCancel={() => {setRemoveXrefModal(false);}}
        onConfirm={onRemoveRow}
        selectedQsrDeptXref={selectedDeptXref}
      />
      <AddQsrDeptMasterModal
        open={addNewQsrDeptMasterModal}
        onClose={closeAddQsrDeptMasterModal}
        onCancel={closeAddQsrDeptMasterModal}
        onConfirm={addNewQsrDeptMaster}
        qsrDeptMasterDescription={qsrDeptMasterDescription}
        handleQsrDeptMasterDescription={handleQsrDeptMasterDescription}
        classOptions={classOptions}
        handleQsrDeptMasterClassNo={handleQsrDeptMasterClassNo}
        formErrors={formErrors}
        />
      <UpdateQsrDeptMasterModal
        open={updateQsrDeptMasterModal}
        onClose={closeUpdateQsrDeptMasterModal}
        onCancel={closeUpdateQsrDeptMasterModal}
        onConfirm={handleUpdateQsrDeptMaster}
        handleQsrDeptMasterDescription={handleUpdateQsrDeptMasterDescription}
        handleQsrDeptMasterClassNo={handleUpdateQsrDeptMasterClassNo}
        formErrors={updateFormErrors}
        selectedQsrDeptMaster={selectedQsrDeptMaster}
        qsrDeptMasterDescription={updateQsrMasterDescription}
        qsrDeptMasterClassNo={updateQsrMasterClassNo?.toString()}
        classOptions={classOptions}
      />
      <RemoveQsrDeptMasterModal
        open={removeMasterModal}
        onClose={closeRemoveMasterModal}
        onCancel={closeRemoveMasterModal}
        onConfirm={handleRemoveQsrMaster}
        selectedQsrDeptMaster={selectedQsrDeptMaster}
        classOptions={classOptions}
        formErrors={updateFormErrors}
      />
      <AddNewQsrXrefModal
        open={addNewQsrDeptXrefModal}
        onClose={closeAddQsrDeptXrefModal}
        onCancel={closeAddQsrDeptXrefModal}
        onConfirm={handleAddQsrDeptXref}
        selectedClassToAddQsrXref={selectedClassToAddQsrXref}
        handleClassSelect={handleClassSelect}
        selectedBase={selectedBase}
        handleBaseSelect={handleBaseSelect}
        selectedSize={selectedSize}
        handleSizeSelect={handleSizeSelect}
        selectedProduct={selectedProduct}
        handleProductSelect={handleProductSelect}
        classOptions={classOptions}
        baseOptions={baseOptions}
        sizeOptions={sizeOptions}
        productOptions={productOptions}
        isDisabled={isDisabled}
        validateSelections={validateCbspToAddQsrXref}
        selectedQsrDeptMaster={selectedQsrDeptMaster}
      />
      <Modal
        id="modifyXrefDepartmentModal"
        open={modifyXrefDepartmentModal}
        onClose={closeModifyDepartmentModal}
        title={`Modify assigned Departments for selected Xrefs`}
      >
        <div>
          <DropdownWrapper>
            <Combobox label={'Desired QSR Department Master'}
                      options={qsrDeptMasters.map(qsrDeptMasterIntoOptions)}
                      value={selectedDesiredQsrMaster}
                      setSelectedOption={(_event: any, newValue: QsrOptionSelect | null) => {
                        setSelectedDesiredQsrMaster(newValue ?? null);
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      renderOption={(props, option) => (
                        <li {...props}>
                          {option.label}
                          {option.isNotAssigned ?
                            <CloseIcon color="error" sx={{ fontSize: 15, paddingLeft: '5px' }} /> :
                            <CheckIcon color="success" sx={{ paddingLeft: '5px' }} />}
                        </li>
                      )}
            />
          </DropdownWrapper>
          <StyledButtons>
            <Button variant={Variant.Secondary} label="Cancel" onClick={closeModifyDepartmentModal} />
            <Button label="Ok" onClick={handleModifyXrefsDepartments}
                    disabled={selectedQsrDeptMaster?.id === selectedDesiredQsrMaster?.id || selectedDesiredQsrMaster === null} />
          </StyledButtons>
        </div>
      </Modal>
    </>
  );
};

export default QsrDept;
